import { z } from 'zod';
import { useEffect } from 'react';
import {
    CommanderFormProps,
    SchemaFormDefinition,
    SchemaFormFields,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { createZodFormResolver, getBase64, getDefaults, lt, Validation } from '@mews/framework';
import { FILE_SCHEMA_OPTIONS, SUPPORTED_FILE_EXTENSIONS } from '../../../common/constants';
import { useGetServiceNamesByIdQuery } from '../../../common/queries/useGetServiceNamesByIdQuery';
import { useAddAttachmentMutation } from '../hooks/useAddAttachmentMutation';
import { useCurrentEnterpriseAttachments } from '../../../common/hooks/useCurrentEnterpriseAttachments';

export const uploadFileSchema = (existingNames: string[]) =>
    z.object({
        file: Validation.File.Default(FILE_SCHEMA_OPTIONS).refine((file) => file.length === 1, {
            message: 'FieldIsRequired',
        }),
        name: z
            .string()
            .min(1)
            .max(255)
            .refine((name) => !existingNames.includes(name), {
                message: 'ContractFormDuplicateNameValidationMessage',
            }),
        services: z.array(z.string()).min(1),
        notes: z.string().max(1000).optional(),
    });

export type UploadFileFormValues = z.infer<ReturnType<typeof uploadFileSchema>>;

const uploadFileFormDefinition = ({
    services,
}: {
    services: Record<string, string>;
}): SchemaFormDefinition<ReturnType<typeof uploadFileSchema>> => ({
    fields: {
        file: {
            label: lt('File'),
            field: { type: 'file', accept: SUPPORTED_FILE_EXTENSIONS },
            required: true,
        },
        name: {
            label: lt('ContractsAttachmentLibraryCustomFileName'),
        },
        services: {
            label: lt('ContractsAttachmentLibraryApplicableServices'),
            field: {
                type: 'select',
                options: Object.entries(services).map(([value, label]) => createOption({ value, label })),
            },
        },
        notes: {
            label: lt('Note'),
            field: {
                type: 'textarea',
                rows: 1,
            },
        },
    },
});

export const UploadFileForm = (props: CommanderFormProps) => {
    const { data: services } = useGetServiceNamesByIdQuery();
    const { mutateAsync: addAttachmentMutation } = useAddAttachmentMutation();

    const attachmentNames = useCurrentEnterpriseAttachments()?.map((attachment) => attachment.customName);
    const schema = uploadFileSchema(attachmentNames);

    const { renderFormContent, watch, setValue } = useHandledDialogFormControls<UploadFileFormValues>({
        ...props,
        defaultValues: getDefaults(schema),
        resolver: createZodFormResolver(schema),
        onSubmit: async (values) => {
            const base64Data = await getBase64(values.file[0]);
            const fileName = values.file[0].name;
            const parameters = {
                file: {
                    name: fileName,
                    mimeType: values.file[0].type,
                    data: base64Data,
                },
                customName: values.name?.trim() === '' ? fileName : values.name,
                serviceIds: values.services,
                notes: values.notes,
            };

            await addAttachmentMutation(parameters);
        },
    });

    const file = watch('file');
    const fileName = file?.[0]?.name;

    useEffect(() => {
        if (fileName != null) {
            setValue('name', fileName);
        }
    }, [fileName, setValue]);

    return renderFormContent(
        <SchemaFormFields schema={schema} formDefinition={uploadFileFormDefinition({ services })} />
    );
};
